import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { useGetContactInfo, Representee } from "api/common/useGetContactInfo";
import { ImpersonationSelect } from "components/UserMenu/ImpersonationSelect";
import { useGetContractIdData } from "providers/ContractIdProvider";
import theme from "tailwindTheme";
import { Avatar } from "./Avatar";

/**
 * @returns Avatar component with the details of the currently selected
 * contact. For representatives (users with representees) the avatar doubles
 * as a "switch user" control: clicking it opens a searchable list of the
 * contacts they can act on behalf of.
 *
 * The switcher lives here, in its own Popover, rather than inside the
 * hamburger UserMenu's Menu: a Headless UI Menu hijacks keyboard input for
 * type-ahead and closes on any item click, which broke the search box.
 */
export const SelectedContactAvatar = () => {
  const { selectedContact, setSelectedContact, setSelectedContactId } =
    useGetContractIdData();
  const { data: contactData } = useGetContactInfo();

  const avatarColors = theme.colors.avatarColors;

  const renderAvatar = () => {
    if (selectedContact && contactData) {
      const indexOfContact =
        contactData?.representees?.findIndex(
          (representee) => representee.id === selectedContact.id
        ) ?? 0;
      //default contact gets 0, and any rep start from 1
      const colorIndex = indexOfContact !== -1 ? indexOfContact + 1 : 0;
      const selectedContactAvatarColor =
        avatarColors[
          ((colorIndex % avatarColors.length) + avatarColors.length) %
            avatarColors.length
        ];

      return (
        <Avatar
          backgroundColor={selectedContactAvatarColor}
          initials={selectedContact?.userName?.charAt(0) ?? ""}
        />
      );
    }
    return <Avatar backgroundColor={"bg-white"} initials={""} />;
  };

  const representees = contactData?.representees ?? [];

  // Plain (non-clickable) avatar when there is nobody to switch to.
  if (representees.length === 0 || !contactData) {
    return renderAvatar();
  }

  // The representative's own contact, so they can switch back from here too.
  const self: Representee = {
    id: contactData.contactId ?? 0,
    contactId: String(contactData._contactId ?? ""),
    name: contactData.name ?? "",
  };
  const switchableContacts = [self, ...representees];

  const selected: Representee | undefined = selectedContact
    ? {
        id: Number(selectedContact.id),
        contactId: String(selectedContact.contactId ?? ""),
        name: selectedContact.userName ?? "",
      }
    : self;

  return (
    <Popover className="grid relative items-center">
      <Popover.Button className="rounded-full focus:outline-none">
        {renderAvatar()}
      </Popover.Button>
      <Transition
        as={Fragment}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Popover.Panel className="absolute top-full right-0 z-30 p-3 mt-2 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right">
          {({ close }) => (
            <ImpersonationSelect
              representees={switchableContacts}
              selected={selected}
              onSelect={(representee) => {
                setSelectedContact({
                  id: representee.id,
                  contactId: representee.contactId,
                  userName: representee.name,
                });
                setSelectedContactId(representee.id);
                close();
              }}
            />
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};
